import React from 'react';
import { AlertTriangle, Wifi, RefreshCw, Search, Frown, ShieldAlert, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface ErrorStateProps {
  onRetry?: () => void;
  className?: string;
}

interface EmptyStateProps {
  onAction?: () => void;
  actionLabel?: string;
  className?: string;
}

// Network/Connection Error
export const NetworkError: React.FC<ErrorStateProps> = ({ onRetry, className = "" }) => (
  <Card className={`text-center ${className}`}>
    <CardHeader>
      <div className="flex justify-center mb-4">
        <Wifi className="h-16 w-16 text-red-500" />
      </div>
      <CardTitle className="text-xl text-slate-900 dark:text-white">
        Connection Problem
      </CardTitle>
      <CardDescription>
        Unable to connect to our servers. Please check your internet connection.
      </CardDescription>
    </CardHeader>
    <CardContent>
      {onRetry && (
        <Button onClick={onRetry} className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4" />
          Try Again
        </Button>
      )}
    </CardContent>
  </Card>
);

// Server Error (5xx)
export const ServerError: React.FC<ErrorStateProps> = ({ onRetry, className = "" }) => (
  <Card className={`text-center ${className}`}>
    <CardHeader>
      <div className="flex justify-center mb-4">
        <AlertTriangle className="h-16 w-16 text-red-500" />
      </div>
      <CardTitle className="text-xl text-slate-900 dark:text-white">
        Server Error
      </CardTitle>
      <CardDescription>
        Something went wrong on our end. We're working to fix this issue.
      </CardDescription>
    </CardHeader>
    <CardContent>
      {onRetry && (
        <Button onClick={onRetry} variant="outline" className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4" />
          Try Again
        </Button>
      )}
    </CardContent>
  </Card>
);

// Not Found Error (404)
export const NotFoundError: React.FC<{
  title?: string;
  description?: string;
  onGoBack?: () => void;
  className?: string;
}> = ({ 
  title = "Page Not Found",
  description = "The page you're looking for doesn't exist or has been moved.",
  onGoBack,
  className = ""
}) => (
  <Card className={`text-center ${className}`}>
    <CardHeader>
      <div className="flex justify-center mb-4">
        <Frown className="h-16 w-16 text-slate-400" />
      </div>
      <CardTitle className="text-xl text-slate-900 dark:text-white">
        {title}
      </CardTitle>
      <CardDescription>
        {description}
      </CardDescription>
    </CardHeader>
    <CardContent>
      {onGoBack && (
        <Button onClick={onGoBack} className="flex items-center gap-2">
          ← Go Back
        </Button>
      )}
    </CardContent>
  </Card>
);

// Access Denied Error (403)
export const AccessDeniedError: React.FC<ErrorStateProps> = ({ onRetry, className = "" }) => (
  <Card className={`text-center ${className}`}>
    <CardHeader>
      <div className="flex justify-center mb-4">
        <ShieldAlert className="h-16 w-16 text-yellow-500" />
      </div>
      <CardTitle className="text-xl text-slate-900 dark:text-white">
        Access Denied
      </CardTitle>
      <CardDescription>
        You don't have permission to access this resource.
      </CardDescription>
    </CardHeader>
    <CardContent>
      {onRetry && (
        <Button onClick={onRetry} variant="outline" className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4" />
          Retry
        </Button>
      )}
    </CardContent>
  </Card>
);

// Timeout Error
export const TimeoutError: React.FC<ErrorStateProps> = ({ onRetry, className = "" }) => (
  <Card className={`text-center ${className}`}>
    <CardHeader>
      <div className="flex justify-center mb-4">
        <Clock className="h-16 w-16 text-orange-500" />
      </div>
      <CardTitle className="text-xl text-slate-900 dark:text-white">
        Request Timeout
      </CardTitle>
      <CardDescription>
        The request took too long to complete. Please try again.
      </CardDescription>
    </CardHeader>
    <CardContent>
      {onRetry && (
        <Button onClick={onRetry} className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4" />
          Try Again
        </Button>
      )}
    </CardContent>
  </Card>
);

// Empty Search Results
export const EmptySearchResults: React.FC<EmptyStateProps & {
  searchTerm?: string;
}> = ({ searchTerm, onAction, actionLabel = "Clear Search", className = "" }) => (
  <Card className={`text-center ${className}`}>
    <CardHeader>
      <div className="flex justify-center mb-4">
        <Search className="h-16 w-16 text-slate-400" />
      </div>
      <CardTitle className="text-xl text-slate-900 dark:text-white">
        No Results Found
      </CardTitle>
      <CardDescription>
        {searchTerm 
          ? `No results found for "${searchTerm}". Try adjusting your search terms.`
          : "No results found. Try adjusting your search terms."
        }
      </CardDescription>
    </CardHeader>
    <CardContent>
      {onAction && (
        <Button onClick={onAction} variant="outline">
          {actionLabel}
        </Button>
      )}
    </CardContent>
  </Card>
);

// Empty Data State
export const EmptyDataState: React.FC<EmptyStateProps & {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
}> = ({ 
  title = "No Data Available",
  description = "There's nothing to show here yet.",
  icon,
  onAction,
  actionLabel = "Add Item",
  className = ""
}) => (
  <Card className={`text-center ${className}`}>
    <CardHeader>
      <div className="flex justify-center mb-4">
        {icon || <Frown className="h-16 w-16 text-slate-400" />}
      </div>
      <CardTitle className="text-xl text-slate-900 dark:text-white">
        {title}
      </CardTitle>
      <CardDescription>
        {description}
      </CardDescription>
    </CardHeader>
    <CardContent>
      {onAction && (
        <Button onClick={onAction}>
          {actionLabel}
        </Button>
      )}
    </CardContent>
  </Card>
);

// Generic Error with custom message
export const GenericError: React.FC<ErrorStateProps & {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
}> = ({ 
  title = "Something went wrong",
  description = "An unexpected error occurred. Please try again.",
  icon,
  onRetry,
  className = ""
}) => (
  <Card className={`text-center ${className}`}>
    <CardHeader>
      <div className="flex justify-center mb-4">
        {icon || <AlertTriangle className="h-16 w-16 text-red-500" />}
      </div>
      <CardTitle className="text-xl text-slate-900 dark:text-white">
        {title}
      </CardTitle>
      <CardDescription>
        {description}
      </CardDescription>
    </CardHeader>
    <CardContent>
      {onRetry && (
        <Button onClick={onRetry} className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4" />
          Try Again
        </Button>
      )}
    </CardContent>
  </Card>
);

// Inline error states for smaller components
export const InlineError: React.FC<{
  message: string;
  onRetry?: () => void;
  className?: string;
}> = ({ message, onRetry, className = "" }) => (
  <div className={`flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg ${className}`}>
    <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
    <span className="text-sm text-red-700 dark:text-red-300 flex-1">{message}</span>
    {onRetry && (
      <Button 
        onClick={onRetry} 
        size="sm" 
        variant="outline" 
        className="text-red-700 border-red-300 hover:bg-red-100 dark:text-red-300 dark:border-red-700 dark:hover:bg-red-900/40"
      >
        Retry
      </Button>
    )}
  </div>
);